import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BounceBackService, BounceBackCoupon } from '../../shared/services/bounceback.service';
import { NotificationService } from '../../shared/services/notification.service';
import { UserService } from '../../shared/services/user.service';
import { CompanyService } from '../../shared/services/company.service';
import { OrderService } from '../../shared/services/order.service';

@Component({
  selector: 'app-bounceback-coupon-input',
  templateUrl: './bounceback-coupon-input.component.html',
  styleUrls: ['./bounceback-coupon-input.component.scss']
})
export class BouncebackCouponInputComponent implements OnInit, OnDestroy {
  @Input() subtotal: number = 0;
  @Input() shippingTotal: number = 0;
  @Input() taxTotal: number = 0;
  @Input() cartItems: any[] = [];
  @Input() storeId: number = 0;
  @Input() disabled: boolean = false;
  
  @Output() couponApplied = new EventEmitter<BounceBackCoupon>();
  @Output() couponRemoved = new EventEmitter<void>();
  @Output() loadingChanged = new EventEmitter<boolean>();

  couponCodeControl = new FormControl('', [
    Validators.required,
    Validators.minLength(3),
    Validators.maxLength(20)
  ]);

  appliedCoupon: BounceBackCoupon | null = null;
  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  discountAmount: number = 0;
  finalTotal: number = 0;

  // Computed property to check if coupon code is complete
  get isCouponCodeComplete(): boolean {
    return this.couponCodeControl.value?.toLowerCase() === 'bounce back';
  }

  // Computed property for button text
  get buttonText(): string {
    return this.appliedCoupon ? 'Reset' : 'Apply';
  }

  // Computed property for button action
  get isResetMode(): boolean {
    return this.appliedCoupon !== null;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private bouncebackService: BounceBackService,
    private notificationService: NotificationService,
    private userService: UserService,
    private companyService: CompanyService,
    private orderService: OrderService
  ) {}

  ngOnInit(): void {
    // Ensure component starts with clean state (no session storage restoration)
    this.initializeCleanState();

    // Set up form control subscription for real-time validation
    this.couponCodeControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(value => {
        // Only clear error messages while typing, don't show success messages
        if (this.errorMessage) {
          this.errorMessage = '';
        }
      });
  }

  /**
   * Initialize component with clean state
   * This ensures the component doesn't restore stale session data on page reload
   */
  private initializeCleanState(): void {
    this.appliedCoupon = null;
    this.discountAmount = 0;
    this.finalTotal = 0;
    this.errorMessage = '';
    this.successMessage = '';
    this.couponCodeControl.setValue('');
    this.setLoading(false);
  }

  /**
   * Handle API error responses and display appropriate error messages
   */
  private handleApiError(result: any): void {
    if (!result) {
      this.errorMessage = 'Failed to apply Bounce Back coupon. Please try again.';
      return;
    }

    // Check for specific error messages from the API response
    let errorMessage = '';

    // Priority order for error messages:
    // 1. Message field (most specific)
    // 2. ErrorDescription field 
    // 3. StatusName field
    // 4. Generic fallback message

    if (result.Message && result.Message.trim()) {
      errorMessage = result.Message;
    } else if (result.ErrorDescription && result.ErrorDescription.trim()) {
      errorMessage = result.ErrorDescription;
    } else if (result.StatusName && result.StatusName !== 'Failed') {
      errorMessage = result.StatusName;
    } else {
      // Fallback based on status code
      switch (result.Status) {
        case 1:
          errorMessage = 'Failed to apply Bounce Back coupon. Please check your eligibility.';
          break;
        case 2:
          errorMessage = 'Invalid coupon code. Please verify and try again.';
          break;
        case 3:
          errorMessage = 'Coupon has expired or is no longer valid.';
          break;
        default:
          errorMessage = 'Failed to apply Bounce Back coupon. Please try again.';
      }
    }

    this.errorMessage = errorMessage;
  }

  /**
   * Handle network/connection errors
   */
  private handleNetworkError(error: any): void {
    let errorMessage = 'Network error. Please check your connection and try again.';

    // Check if this is an HTTP 500 error with API response data
    if (error?.status === 500 && error?.error) {
      // Try to extract the API error message from the response body
      if (error.error.Message) {
        errorMessage = error.error.Message;
      } else if (error.error.ErrorDescription) {
        errorMessage = error.error.ErrorDescription;
      } else if (typeof error.error === 'string') {
        // Sometimes the error might be a string
        try {
          const parsedError = JSON.parse(error.error);
          if (parsedError.Message) {
            errorMessage = parsedError.Message;
          }
        } catch (parseError) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else {
        errorMessage = 'Server error. Please try again later.';
      }
    } else if (error?.status === 0) {
      errorMessage = 'Unable to connect to server. Please check your internet connection.';
    } else if (error?.status === 404) {
      errorMessage = 'Service not found. Please contact support.';
    } else if (error?.status === 503) {
      errorMessage = 'Service temporarily unavailable. Please try again later.';
    } else if (error?.message) {
      // Use error message if available
      errorMessage = `Error: ${error.message}`;
    }

    this.errorMessage = errorMessage;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle button click - either apply coupon or reset
   */
  onButtonClick(): void {
    if (this.isResetMode) {
      this.resetCoupon();
    } else {
      this.applyCoupon();
    }
  }

  /**
   * Reset applied coupon and return to apply mode
   */
  resetCoupon(): void {
    // Clear applied coupon state
    this.appliedCoupon = null;
    this.discountAmount = 0;
    this.finalTotal = 0;
    this.clearMessages();
    
    // Clear the input field
    this.couponCodeControl.setValue('');
    
    // Remove from session storage
    this.removeCouponFromStorage();
    
    // Emit coupon removed event
    this.couponRemoved.emit();
    
    // Recalculate order without coupon
    this.orderService.calculateOrder();
  }

  /**
   * Reset component state to Apply mode (used on errors)
   */
  private resetComponentState(): void {
    // Clear applied coupon state
    this.appliedCoupon = null;
    this.discountAmount = 0;
    this.finalTotal = 0;
    
    // Remove from session storage and user service
    this.removeCouponFromStorage();
    
    // Note: Don't clear input field or emit events on error - user might want to retry
    // This ensures the button returns to "Apply" mode even after API errors
  }

  /**
   * Apply BounceBack coupon code
   */
  applyCoupon(): void {
    const couponCode = this.couponCodeControl.value?.trim();
    
    if (!couponCode) {
      this.errorMessage = 'Please enter a coupon code';
      return;
    }

    // Frontend validation: Check if it's a bounceback coupon
    if (!this.bouncebackService.isBounceBackCoupon(couponCode)) {
      this.errorMessage = 'Invalid coupon code. Please enter "bounceback" to use your rewards.';
      return;
    }

    this.setLoading(true);
    this.clearMessages();

    const customerId = this.userService.userServiceModal.customerData.CustomerId || 0;

    // Basic validation - check minimum order amount
    if (this.subtotal < 25) {
              this.errorMessage = 'Minimum order amount of $25 required for Bounce Back rewards.';
      this.setLoading(false);
      return;
    }

    // Clear any existing bounce back coupons from user service to avoid duplicates
    if (this.userService.userServiceModal.couponInfo.Allcoupons) {
      this.userService.userServiceModal.couponInfo.Allcoupons = this.userService.userServiceModal.couponInfo.Allcoupons.filter(
        code => !this.bouncebackService.isBounceBackCoupon(code)
      );
    }

    // Store BounceBack coupon in session storage temporarily for API call
    const bounceBackCoupon: BounceBackCoupon = {
      code: couponCode,
      discount: 0, // Will be calculated by backend
      type: 'percentage',
      isValid: true,
      message: 'Bounce Back coupon applied'
    };
    sessionStorage.setItem('bounceBackAppliedCoupon', JSON.stringify(bounceBackCoupon));

    // Call the existing order calculation API which includes BounceBack coupons
    
    this.orderService.calculateOrder().then((result) => {
      if (result && result.Status === 0) {
        // Success - coupon applied
        this.appliedCoupon = bounceBackCoupon;
        
        // Extract discount information from the API response
        const responseData = result.Data;
        
        // Calculate discount amount from the response
        // The API response shows DiscountTotal which is the bounceback discount amount
        this.discountAmount = responseData.DiscountTotal || 0;
        
        // Update the coupon with the actual discount percentage for display consistency
        bounceBackCoupon.discount = this.discountPercentage;
        
        // Set final total from API response
        this.finalTotal = responseData.Total || 0;
        
        // Update session storage with the actual discount amount
        sessionStorage.setItem('bounceBackAppliedCoupon', JSON.stringify(bounceBackCoupon));
        sessionStorage.setItem('bounceBackDiscountAmount', this.discountAmount.toString());
        
        this.successMessage = `Bounce Back coupon applied successfully! Discount: ${this.bouncebackService.formatCurrency(this.discountAmount, this.companyService.selectedCurrency.CurrencyCode)}`;
        
        // Emit the applied coupon with actual discount amount
        this.couponApplied.emit(bounceBackCoupon);
        
        // Clear the input
        this.couponCodeControl.setValue('');
              } else {
          // Error from API - show dynamic error message
          this.handleApiError(result);
          
          // Reset component state to Apply mode
          this.resetComponentState();
        }
      this.setLoading(false);
    }).catch((error) => {
      // Handle network/connection errors
      this.handleNetworkError(error);
      this.setLoading(false);
      
      // Reset component state to Apply mode
      this.resetComponentState();
    });
  }

  /**
   * Remove applied coupon
   */
  removeCoupon(): void {
    this.appliedCoupon = null;
    this.discountAmount = 0;
    this.finalTotal = 0;
    this.clearMessages();
    
    // Remove BounceBack coupon from user service
    this.removeCouponFromStorage();
    
    // Recalculate order without BounceBack coupon
    this.orderService.calculateOrder();
    
    // Emit coupon removed event
    this.couponRemoved.emit();
  }

  /**
   * Clear success and error messages
   */
  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  /**
   * Set loading state
   */
  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    this.loadingChanged.emit(loading);
  }

  /**
   * Check if button can be clicked (either apply or reset)
   */
  get canApplyCoupon(): boolean {
    if (this.isResetMode) {
      // In reset mode, button is always enabled (unless loading or disabled)
      return !this.isLoading && !this.disabled;
    } else {
      // In apply mode, check if coupon code is valid
      const code: string = (this.couponCodeControl.value || '').toString().trim();
      return this.couponCodeControl.valid &&
             code.length > 0 &&
             !this.isLoading &&
             !this.disabled;
    }
  }

  /**
   * Get formatted discount amount
   */
  get formattedDiscountAmount(): string {
    return this.bouncebackService.formatCurrency(this.discountAmount, this.companyService.selectedCurrency.CurrencyCode);
  }

  /**
   * Get formatted final total
   */
  get formattedFinalTotal(): string {
    return this.bouncebackService.formatCurrency(this.finalTotal, this.companyService.selectedCurrency.CurrencyCode);
  }

  /**
   * Get formatted subtotal
   */
  get formattedSubtotal(): string {
    return this.bouncebackService.formatCurrency(this.subtotal, this.companyService.selectedCurrency.CurrencyCode);
  }

  /**
   * Calculate discount percentage based on discount amount and API response subtotal
   */
  get discountPercentage(): number {
    // Use the same calculation method as checkout component for consistency
    const apiSubTotal = this.orderService.calculateOrderResponse?.SubTotal || this.subtotal;
    if (apiSubTotal > 0 && this.discountAmount > 0) {
      return Math.round((this.discountAmount / apiSubTotal) * 100);
    }
    return 0;
  }

  /**
   * Get formatted discount percentage
   */
  get formattedDiscountPercentage(): string {
    return `${this.discountPercentage}%`;
  }

  /**
   * Remove BounceBack coupon from storage and user service
   */
  private removeCouponFromStorage(): void {
    // Remove from session storage
    sessionStorage.removeItem('bounceBackAppliedCoupon');
    sessionStorage.removeItem('bounceBackDiscountAmount');
    
    // Remove any bounce back coupons from user service
    if (this.userService.userServiceModal.couponInfo.Allcoupons) {
      this.userService.userServiceModal.couponInfo.Allcoupons = this.userService.userServiceModal.couponInfo.Allcoupons.filter(
        code => !this.bouncebackService.isBounceBackCoupon(code)
      );
    }
  }

  /**
   * Test method to simulate different API error responses (for development/testing only)
   * Usage in browser console: component.testErrorResponse('eligibility')
   */
  public testErrorResponse(errorType: 'eligibility' | 'invalid' | 'expired' | 'network' | 'custom', customMessage?: string): void {
    if (typeof window !== 'undefined' && (window as any).location.hostname === 'localhost') {
      let mockResult: any;

      switch (errorType) {
        case 'eligibility':
          mockResult = {
            Status: 1,
            StatusName: "Failed",
            ErrorDescription: "",
            Message: "You are not eligible to use this coupon.",
            ErrorTransactionId: null
          };
          break;
        case 'invalid':
          mockResult = {
            Status: 2,
            StatusName: "Failed",
            ErrorDescription: "Invalid coupon code",
            Message: "The coupon code you entered is not valid.",
            ErrorTransactionId: null
          };
          break;
        case 'expired':
          mockResult = {
            Status: 3,
            StatusName: "Failed",
            ErrorDescription: "Coupon expired",
            Message: "This coupon has expired and can no longer be used.",
            ErrorTransactionId: null
          };
          break;
        case 'network':
          this.handleNetworkError({ status: 0, message: 'Network connection failed' });
          this.resetComponentState(); // Ensure button returns to Apply mode
          return;
        case 'custom':
          mockResult = {
            Status: 1,
            StatusName: "Failed",
            ErrorDescription: "",
            Message: customMessage || "Custom error message for testing",
            ErrorTransactionId: null
          };
          break;
        default:
          console.warn('Invalid error type. Use: eligibility, invalid, expired, network, or custom');
          return;
      }

      this.handleApiError(mockResult);
      this.resetComponentState(); // Ensure button returns to Apply mode
    } else {
      console.warn('Test method only available in development environment');
    }
  }
}
